import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, CheckCircle2, RefreshCw, WandSparkles, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  StepActions,
  StepActionButton,
  RecordConsents,
  DiscreteActionsDropdown,
} from '@/components';
import ConsentStatus from './ConsentStatus';
import { useToast } from '@/components/hooks/use-toast';
import {
  fetchApplicationConsentStatusByClientPolicyApplicationIdRequest,
  postConsentRecordRequest,
  postRecordCarePacketDownloadRequest,
  selectApplicationConsentStatusData,
  selectApplicationConsentStatusIsLoading,
  selectPostConsentRecordData,
  selectPostConsentRecordErrorMessage,
  postSendConsentNotificationEmailRequest,
  selectSendConsentNotificationEmailData,
  selectSendConsentNotificationEmailError,
  selectPostRecordCarePacketDownloadData,
  selectPostRecordCarePacketDownloadIsLoading,
  selectPostRecordCarePacketDownloadErrorMessage,
  clearSendConsentNotificationEmail,
  clearPostConsentRecord,
  clearPostRecordCarePacketDownload,
  selectSessionAdvisor,
  selectMaybeClientPartner,
  putClientPolicyApplicationStepRequest,
  clearUnderwritingError,
} from '@client/model';
import {
  selectSession,
  selectClient,
  selectPolicyApplicationsError,
  selectPolicyApplicationsStatus,
} from '@client/model/selectors';
import { useClientPolicyApplication } from '@/hooks/useClientPolicyApplication';
import { FundingPolicyType, SessionType } from '@shared';
import { downloadFile } from '@/util/secureDownload';
import { getUserIpAddress } from '@/util/getUserIpAddress';

interface ConsentFormStepProps {
  clientPolicyApplicationId: string;
  consentType: 'submit' | 'start';
  stepType: 'preapplication-consent-form' | 'consent-form';
  stepStatus?: PolicyApplicationStep['status'];
}

interface DownloadCarePacketButtonProps {
  applicationId: string;
  filesToDownload: string[];
  text?: string;
  onDownloadComplete: () => void;
}

export const DownloadCarePacketButton = ({
  filesToDownload,
  onDownloadComplete,
  applicationId,
  text = 'Download Take Home Packet to Continue',
}: DownloadCarePacketButtonProps) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const client = useSelector(selectClient);
  const postRecordCarePacketDownloadData = useSelector(selectPostRecordCarePacketDownloadData);
  const postRecordCarePacketDownloadIsLoading = useSelector(
    selectPostRecordCarePacketDownloadIsLoading,
  );
  const postRecordCarePacketDownloadError = useSelector(
    selectPostRecordCarePacketDownloadErrorMessage,
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadComplete = useCallback(() => {
    dispatch(
      postRecordCarePacketDownloadRequest({
        clientPolicyApplicationId: applicationId,
        clientId: client?.clientId || '',
        downloadedFiles: filesToDownload,
        metadata: {
          downloadCompletedAt: new Date().toISOString(),
          fileCount: filesToDownload.length,
        },
      }),
    );

    onDownloadComplete();
  }, [dispatch, applicationId, client?.clientId, filesToDownload, onDownloadComplete]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);

    try {
      for (let i = 0; i < filesToDownload.length; i++) {
        const url = filesToDownload[i];
        const filename = `${applicationId}-document-${i + 1}.pdf`;
        const result = await downloadFile(url, filename);

        if (!result.success) {
          throw new Error(result.error || 'Failed to download care packet');
        }
      }

      toast({
        title: 'Download Complete',
        description: 'Care packet documents have been downloaded successfully.',
        className: 'bg-green-50 text-green-800',
      });

      handleDownloadComplete();
    } catch (error) {
      toast({
        title: 'Download Failed',
        description:
          error instanceof Error ? error.message : 'Failed to download care packet documents.',
        className: 'bg-red-50 text-red-800',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [filesToDownload, handleDownloadComplete, toast]);

  useEffect(() => {
    if (postRecordCarePacketDownloadData) {
      dispatch(clearPostRecordCarePacketDownload());
    }
  }, [postRecordCarePacketDownloadData, dispatch]);

  useEffect(() => {
    if (postRecordCarePacketDownloadError) {
      dispatch(clearPostRecordCarePacketDownload());
    }
  }, [postRecordCarePacketDownloadError, dispatch]);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleDownload}
      disabled={isDownloading}
      className="cursor-pointer gap-2"
    >
      <Download className="h-4 w-4 cursor-pointer" />
      {isDownloading ? 'Downloading...' : text}
    </Button>
  );
};

export function ConsentFormStep({
  clientPolicyApplicationId,
  consentType,
  stepType,
  stepStatus,
}: ConsentFormStepProps) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { policyApplication } = useClientPolicyApplication();

  const session = useSelector(selectSession);
  const client = useSelector(selectClient);
  const consentStatusData = useSelector(selectApplicationConsentStatusData);
  const isLoadingStatus = useSelector(selectApplicationConsentStatusIsLoading);
  const postConsentRecordData = useSelector(selectPostConsentRecordData);
  const postConsentRecordError = useSelector(selectPostConsentRecordErrorMessage);
  const emailNotificationData = useSelector(selectSendConsentNotificationEmailData);
  const emailNotificationError = useSelector(selectSendConsentNotificationEmailError);
  const policyApplicationError = useSelector(selectPolicyApplicationsError);
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);

  const [showDownloadCarePacketButton, setShowDownloadCarePacketButton] = useState(
    consentType === 'start',
  );

  const [isCarePacketDownloaded, setIsCarePacketDownloaded] = useState(false);

  // State to track batch consent submission
  const [batchSubmission, setBatchSubmission] = useState({
    isActive: false,
    total: 0,
    successful: 0,
    failed: 0,
  });

  const handleDownloadCarePacket = useCallback(() => {
    setIsCarePacketDownloaded(true);
    setShowDownloadCarePacketButton(false);
  }, []);

  const fetchConsentStatus = useCallback(() => {
    dispatch(
      fetchApplicationConsentStatusByClientPolicyApplicationIdRequest({
        clientPolicyApplicationId,
        queryParams: { consentType },
      }),
    );
  }, [dispatch, clientPolicyApplicationId, consentType]);

  useEffect(() => {
    fetchConsentStatus();
  }, [fetchConsentStatus]);

  // Sync state with consentStatusData changes
  useEffect(() => {
    if (consentStatusData) {
      setShowDownloadCarePacketButton(
        consentType === 'start' && !consentStatusData?.hasRecievedCarePacket,
      );
      setIsCarePacketDownloaded(consentStatusData?.hasRecievedCarePacket || false);
    }
  }, [consentStatusData, policyApplication?.fundingPolicyType, consentType]);

  const currentUser = useMemo(() => {
    if (!session || !consentStatusData) return null;

    const isAdvisor = !!session.advisor;
    const clientId = client.clientId;

    if (isAdvisor) {
      return {
        type: 'advisor' as const,
        isPending: consentStatusData.allConsents.advisor.isPending,
        userId: consentStatusData.allConsents.advisor.userId,
      };
    }

    if (clientId) {
      const isMainClient = consentStatusData.allConsents.client.userId === clientId;
      const isPartnerClient = consentStatusData.allConsents?.partnerClient?.userId === clientId;

      if (isMainClient) {
        return {
          type: 'client' as const,
          isPending: consentStatusData.allConsents.client.isPending,
          userId: clientId,
          hasRecievedCarePacket:
            consentStatusData.allConsents.client?.hasRecievedCarePacket === true,
        };
      }

      if (isPartnerClient) {
        return {
          type: 'partnerClient' as const,
          isPending: consentStatusData.allConsents.partnerClient!.isPending,
          userId: clientId,
        };
      }
    }

    return null;
  }, [session, client, consentStatusData]);

  useEffect(() => {
    if (emailNotificationData) {
      toast({
        title: 'Email Sent',
        description: 'Consent reminder email has been sent successfully.',
        className: 'bg-green-50 text-green-800',
      });
      dispatch(clearSendConsentNotificationEmail());
    }
  }, [emailNotificationData, toast, dispatch]);

  useEffect(() => {
    if (emailNotificationError) {
      toast({
        title: 'Failed to Send Email',
        description: 'Could not send the reminder email. Please try again.',
        className: 'bg-red-50 text-red-800',
      });
      dispatch(clearSendConsentNotificationEmail());
    }
  }, [emailNotificationError, toast, dispatch]);

  // Handle putClientPolicyApplicationStep errors
  useEffect(() => {
    if (policyApplicationStatus === 'savingStepStatusError' && policyApplicationError) {
      toast({
        title: 'Failed to Continue',
        description: policyApplicationError,
        className: 'bg-red-50 text-red-800',
      });
      dispatch(clearUnderwritingError());
    }
  }, [policyApplicationStatus, policyApplicationError, toast, dispatch]);

  useEffect(() => {
    if (postConsentRecordData && batchSubmission.isActive) {
      // Increment successful count
      setBatchSubmission(prev => ({
        ...prev,
        successful: prev.successful + 1,
      }));

      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordData, batchSubmission.isActive, dispatch]);

  useEffect(() => {
    if (postConsentRecordError && batchSubmission.isActive) {
      // Increment failed count
      setBatchSubmission(prev => ({
        ...prev,
        failed: prev.failed + 1,
      }));

      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordError, batchSubmission.isActive, dispatch]);

  // Handle batch completion
  useEffect(() => {
    if (
      batchSubmission.isActive &&
      batchSubmission.successful + batchSubmission.failed === batchSubmission.total
    ) {
      if (batchSubmission.failed === 0) {
        toast({
          title: 'Consents Recorded',
          description: 'Your consents have been successfully recorded.',
          className: 'bg-green-50 text-green-800',
        });
        fetchConsentStatus();
      } else {
        toast({
          title: 'Error Recording Consents',
          description: 'Failed to record your consents. Please try again.',
          className: 'bg-red-50 text-red-800',
        });
      }
      setBatchSubmission({
        isActive: false,
        total: 0,
        successful: 0,
        failed: 0,
      });
    }
  }, [batchSubmission, toast, fetchConsentStatus]);

  const handleSubmitConsent = useCallback(
    async (consentIds: string[]) => {
      // Initialize batch tracking
      setBatchSubmission({
        isActive: true,
        total: consentIds.length,
        successful: 0,
        failed: 0,
      });

      const ipAddress = await getUserIpAddress();

      consentIds.forEach(consentId =>
        dispatch(
          postConsentRecordRequest({
            clientId: client.clientId,
            clientPolicyApplicationId,
            resourceType: 'ClientPolicyApplicationConsents',
            consentId,
            consentType,
            ipAddress,
            userAgent: navigator.userAgent,
            metadata: {
              stepType: 'consent-form',
              clientPolicyApplicationId,
            },
          }),
        ),
      );
    },
    [client, dispatch, clientPolicyApplicationId, consentType],
  );

  const handleSkipStep = useCallback(() => {
    dispatch(
      putClientPolicyApplicationStepRequest({
        clientPolicyApplicationId,
        stepType,
        status: 'skipped',
      }),
    );
  }, [dispatch, clientPolicyApplicationId, stepType]);

  if (isLoadingStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto mb-4 h-8 w-8 animate-spin" />
          <p className="text-gray-700">Loading consent status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-6 p-2">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Waterlily Consent Agreements</h3>
          <p className="text-sm text-gray-700">
            Please review and agree to the following consent statements to proceed with your
            application.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchConsentStatus}
          disabled={isLoadingStatus}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoadingStatus ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {currentUser && currentUser.isPending ? (
        <RecordConsents
          userType={currentUser.type}
          onSubmit={consentIds => handleSubmitConsent(consentIds)}
          submitButtonProps={{ disabled: batchSubmission.isActive }}
          consentType={consentType}
          showPdf={
            consentType === 'start' &&
            currentUser.type !== 'advisor' && // only show pdf for clients & partner
            policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
          }
          isCareScout={
            policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
          }
        />
      ) : (
        <ConsentStatus
          consentStatusData={consentStatusData}
          currentUser={currentUser}
          clientPolicyApplicationId={clientPolicyApplicationId}
          consentType={consentType}
        />
      )}

      <StepActions>
        {session.sessionType === SessionType.wizard && stepStatus !== 'skipped' && (
          <DiscreteActionsDropdown
            actions={[
              {
                id: 'skip',
                label: 'Skip Step',
                icon: <WandSparkles className="h-4 w-4" />,
                onClick: handleSkipStep,
                disabled: policyApplicationStatus === 'savingStepStatus',
                isLoading: policyApplicationStatus === 'savingStepStatus',
                loadingText: 'Processing...',
              },
            ]}
          />
        )}
        {showDownloadCarePacketButton &&
          currentUser &&
          currentUser?.type !== 'advisor' &&
          !currentUser?.hasRecievedCarePacket && (
            <DownloadCarePacketButton
              applicationId={clientPolicyApplicationId}
              filesToDownload={consentStatusData?.carePacketFileUrls || []}
              onDownloadComplete={handleDownloadCarePacket}
            />
          )}
        {consentStatusData?.applicationReady &&
          ((currentUser && currentUser?.type == 'advisor') ||
            (!showDownloadCarePacketButton && isCarePacketDownloaded)) && (
            <StepActionButton
              onClick={() => {
                dispatch(
                  putClientPolicyApplicationStepRequest({
                    clientPolicyApplicationId,
                    stepType,
                    status: 'completed',
                  }),
                );
              }}
              icon={<CheckCircle2 className="h-5 w-5" />}
            >
              Continue to Next Step
            </StepActionButton>
          )}
      </StepActions>
    </div>
  );
}
